import { messages } from "../localization/messages.js";
import { constants } from "../utils/Constants";
import BodyFactory from "./BodyFactory";
import { httpHeaders } from "./httpHeaders";
import { httpMethods } from "./httpMethods";
import UrlFactory from "./UrlFactory";

const HttpStatus = require("http-status-codes");

class WebService {
  constructor() {
    this.urlFactory = new UrlFactory();
    this.bodyFactory = new BodyFactory();
  }

  _httpRequest = (
    method,
    headers = undefined,
    body = undefined,
    shouldIncludeConfig
  ) => {
    var requestConfig = {
      method: method,
      body: body,
      signal: this._signal
    };

    if (shouldIncludeConfig) {
      requestConfig.credentials = "include";
      requestConfig.headers = headers;
    }

    return requestConfig;
  };

  _responseCallBack = successCallback => {
    return response => {
      if (!response.ok) {
        throw response;
      }
      return successCallback(response);
    };
  };

  _defaultSuccessCallback = response => {
    return response.json();
  };

  blankSuccessCallback = response => {
    return response;
  };

  _downloadDataCallback = response => {
    window.location.href = response.url;
  };

  _defaultResponseCallBack = () => {
    this._responseCallBack(this._defaultFailureCallback);
  };

  _defaultFailureCallback = error => {
    console.log(
      "HTTP error, status = " + error.status + " message = " + error.text
    );
  };

  _defaultHttpHeaders = contentType => {
    var headers = {};

    if (contentType) {
      headers[httpHeaders.CONTENT_TYPE] = contentType;
    }
    headers[httpHeaders.ACCEPT] = httpHeaders.acceptTypes.ALL;

    return headers;
  };

  _serverErrorCallback(failureCallback, response) {
    const clientError =
      (response.ok || !response.status) &&
      response.message !== constants.FETCH_EXCEPTION_MESSAGE;
    const message = {
      status: clientError ? undefined : HttpStatus.INTERNAL_SERVER_ERROR,
      statusText: clientError
        ? undefined
        : HttpStatus.getStatusText(HttpStatus.INTERNAL_SERVER_ERROR),
      text: clientError ? messages.clientError : messages.serverError,
      code: clientError
        ? constants.CLIENT_ERROR_CODE
        : constants.SERVER_ERROR_CODE
    };
    failureCallback(message);
  }

  _failureCallback(failureCallback) {
    return response => {
      try {
        response.json().then(body => {
          const error = {
            status: response.status,
            statusText: response.statusText,
            text: body.title,
            code: body.code
          };
          failureCallback(error);
        });
      } catch (error) {
        this._serverErrorCallback(failureCallback, response);
      }
    };
  }

  async _sendRequest(
    url,
    method,
    data = undefined,
    dataCallback,
    successCallback = this._defaultSuccessCallback,
    failureCallback = this._defaultFailureCallback,
    contentType = httpHeaders.contentTypes.APPLICATION_JSON,
    shouldIncludeConfig = true
  ) {
    this._abortController = window.AbortController && new AbortController();
    this._signal = this._abortController && this._abortController.signal;

    return fetch(
      url,
      this._httpRequest(
        method,
        this._defaultHttpHeaders(contentType),
        data,
        shouldIncludeConfig
      )
    )
      .then(this._responseCallBack(successCallback))
      .then(dataCallback)
      .catch(this._failureCallback(failureCallback));
  }

  async get(
    url,
    dataCallback,
    successCallback = undefined,
    failureCallback = undefined
  ) {
    return this._sendRequest(
      url,
      httpMethods.GET,
      undefined,
      dataCallback,
      successCallback,
      failureCallback,
      null
    );
  }

  async download(url, failureCallback = undefined) {
    return this.get(
      url,
      this._downloadDataCallback,
      this.blankSuccessCallback,
      failureCallback
    );
  }

  async put(
    url,
    data = undefined,
    dataCallback,
    successCallback = undefined,
    failureCallback = undefined
  ) {
    return this._sendRequest(
      url,
      httpMethods.PUT,
      JSON.stringify(data),
      dataCallback,
      successCallback,
      failureCallback
    );
  }

  async post(
    url,
    data = undefined,
    dataCallback,
    successCallback = undefined,
    failureCallback = undefined,
    needsJSONparsing = true,
    contentType = httpHeaders.contentTypes.APPLICATION_JSON
  ) {
    data = needsJSONparsing ? JSON.stringify(data) : data;
    return this._sendRequest(
      url,
      httpMethods.POST,
      data,
      dataCallback,
      successCallback,
      failureCallback,
      contentType
    );
  }

  async delete(
    url,
    dataCallback,
    successCallback = this.blankSuccessCallback,
    failureCallback = undefined
  ) {
    return this._sendRequest(
      url,
      httpMethods.DELETE,
      undefined,
      dataCallback,
      successCallback,
      failureCallback
    );
  }

  async patch(
    url,
    data,
    dataCallback,
    successCallback = undefined,
    failureCallback = undefined
  ) {
    return this._sendRequest(
      url,
      httpMethods.PATCH,
      JSON.stringify(data),
      dataCallback,
      successCallback,
      failureCallback,
      httpHeaders.contentTypes.APPLICATION_JSON_PATCH
    );
  }

  abortFetching() {
    this._abortController && this._abortController.abort();
  }
}

export default WebService;
